import { gql } from '@urql/core';
import { createQueryKeys } from '@lukemorales/query-key-factory';

export const goalsFeatureKeys = createQueryKeys('goalsFeature', {
  goalListSummary: ({ siteId, search, limit, order, sort }) => [
    { siteId, search, limit, order: { field: order, sort } },
  ],
  goalCardList: ({ siteId, search, limit }) => [{ siteId, search, limit }],
  goalRefList: ({ siteId }) => [{ siteId }],
  goalSelectorList: ({ siteId }) => [{ siteId }],
  goalLinked: ({ id }) => [{ id }],
  goalDetail: ({ id, presetDate, startDate, endDate }) => [{ id, presetDate, startDate, endDate }],
  goalDistribution: ({ id, startDate, endDate, timeUnit }) => [{ id, startDate, endDate, timeUnit }],
  goalCounts: ({ id, startDate, endDate }) => [{ id, startDate, endDate }],
  goalPageConversions: ({ id, siteId, startDate, endDate, search, page, perPage, order }) => [
    { id, siteId, startDate, endDate, search, page, perPage, order },
  ],
});

export const goalListSummaryQuery = ({ siteId, search, limit, order, sort }) => ({
  ...goalsFeatureKeys.goalListSummary({ siteId, search, limit, order, sort }),
  getNextPageParam: (lastPage) =>
    lastPage.goalListSummary.hasNextPage ? lastPage.goalListSummary.nextPageCursor : undefined,
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalListSummary($siteId: Int, $cursor: String, $search: String, $limit: Int, $order: GoalListOrder) {
        goalListSummary(site: $siteId, cursor: $cursor, search: $search, limit: $limit, order: $order) {
          list {
            triggers {
              id
              type
              filter
              deletedAt
            }
            id
            siteId
            name
            abTestsCountActive
            recentConversionsCount
            createdAt
            createdBy {
              login
              name
            }
            sessionsCount
          }
          hasNextPage
          nextPageCursor
        }
      }
    `,
  },
});

export const goalCardListQuery = ({ siteId, search, limit }) => ({
  ...goalsFeatureKeys.goalCardList({ siteId, search, limit }),
  getNextPageParam: (lastPage) =>
    lastPage.goalCardList.hasNextPage ? lastPage.goalCardList.nextPageCursor : undefined,
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalCardList($siteId: Int, $cursor: String, $search: String, $limit: Int) {
        goalCardList(site: $siteId, cursor: $cursor, search: $search, limit: $limit) {
          list {
            id
            siteId
            name
            triggers {
              id
              type
              filter
              deletedAt
            }
            abTestsCount
            abTestsCountActive
          }
          hasNextPage
          nextPageCursor
        }
      }
    `,
  },
});

export const goalRefListQuery = ({ siteId }) => ({
  ...goalsFeatureKeys.goalRefList({ siteId }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalRefList($siteId: Int) {
        goalRefList(site: $siteId) {
          id
          siteId
          name
          hasValue
          createdAt
        }
      }
    `,
  },
});

export const goalSelectorListQuery = ({ siteId }) => ({
  ...goalsFeatureKeys.goalSelectorList({ siteId }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalSelectorList($siteId: Int) {
        goalSelectorList(site: $siteId) {
          id
          siteId
          name
          hasValue
          createdAt
          deletedAt
          triggers {
            id
            type
            filter
            deletedAt
          }
        }
      }
    `,
  },
});

export const goalLinkedQuery = ({ id }) => ({
  ...goalsFeatureKeys.goalLinked({ id }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalLinked($id: Int!) {
        goalLinked(id: $id) {
          id
          siteId
          name
          hasValue
          createdAt
          deletedAt
          triggers {
            id
            type
            filter
            deletedAt
          }
        }
      }
    `,
  },
});

export const GOALS_BY_IDS_NAME_ONLY = gql`
  query GoalsByIds($ids: [Int!]!) {
    goalsByIds(ids: $ids) {
      id
      name
    }
  }
`;

export const goalDetailQuery = ({ id, startDate, endDate, presetDate }) => ({
  ...goalsFeatureKeys.goalDetail({ id, startDate, endDate, presetDate }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalDetail($id: Int!, $startDate: Int, $endDate: Int, $presetDate: String) {
        goalDetail(id: $id, startDate: $startDate, endDate: $endDate, presetDate: $presetDate) {
          __typename
          id
          name
          abTestsCount
          abTestsCountActive
          autoName
          createdAt
          createdBy {
            id
            name
            login
          }
          deletedAt
          thumbnailUrl
          triggers {
            autoName
            createdAt
            deletedAt
            filter
            id
            name
            valueType
            type
            fixedValue {
              worth
              currency
            }
            elementValue {
              currency
              condition {
                trackingOption
                elementSelectorType
                pageUrl
                wildcardUrl
                onPage
                onAnyPage
                selectorForDesktop
                selectorForTablet
                selectorForPhone
              }
            }
          }
          siteId
          syncValues
          hasValue
        }
      }
    `,
  },
});

export const goalPageConversionsQuery = ({ id, siteId, startDate, endDate, search, page, perPage, order }) => ({
  ...goalsFeatureKeys.goalPageConversions({
    id,
    siteId,
    startDate,
    endDate,
    search,
    page,
    perPage,
    order,
  }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalPageConversions(
        $id: Int!
        $siteId: Int!
        $startDate: Int
        $endDate: Int
        $search: String
        $page: Int
        $perPage: Int
        $order: GoalPageConversionsOrder
      ) {
        goalPageConversions(
          id: $id
          siteId: $siteId
          startDate: $startDate
          endDate: $endDate
          search: $search
          page: $page
          perPage: $perPage
          order: $order
        ) {
          list {
            pageUrl
            totalConversions
            totalVisitors
            totalValue {
              displayWorth
              displayCurrency
            }
          }
          meta {
            page
            perPage
            total
            totalPages
          }
        }
      }
    `,
  },
});

export const goalDistributionQuery = ({ id, startDate, endDate, timeUnit }) => ({
  ...goalsFeatureKeys.goalDistribution({ id, startDate, endDate, timeUnit }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalDistribution($id: Int!, $startDate: Int, $endDate: Int, $timeUnit: TimeUnit!) {
        goalDistribution(id: $id, startDate: $startDate, endDate: $endDate, timeUnit: $timeUnit) {
          conversionsCount
          uniqueConversionsCount
          startAt
          endAt
          totalValue {
            displayWorth
            displayCurrency
          }
          triggers {
            id
            conversionsCount
            uniqueConversionsCount
            totalValue {
              displayWorth
              displayCurrency
            }
          }
        }
      }
    `,
  },
});

export const goalCountsQuery = ({ id, startDate, endDate }) => ({
  ...goalsFeatureKeys.goalCounts({ id, startDate, endDate }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalCounts($id: Int!, $startDate: Int, $endDate: Int) {
        goalCounts(id: $id, startDate: $startDate, endDate: $endDate) {
          conversionsCount
          uniqueConversionsCount
          totalValue {
            displayWorth
            displayCurrency
          }
          triggers {
            id
            conversionsCount
            uniqueConversionsCount
            totalValue {
              displayWorth
              displayCurrency
            }
          }
        }
      }
    `,
  },
});
